import { jsx as _jsx } from "react/jsx-runtime";
/**
 * eStream React Hooks
 *
 * React bindings for the eStream Browser SDK.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef, } from 'react';
import { EstreamClient } from './client.js';
// ============================================================================
// Context
// ============================================================================
const EstreamContext = createContext(null);
/**
 * Provider component for eStream client
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <EstreamProvider config={{ url: 'https://edge.estream.dev:4433' }}>
 *       <MyApp />
 *     </EstreamProvider>
 *   );
 * }
 * ```
 */
export function EstreamProvider({ config, children }) {
    const client = useMemo(() => new EstreamClient({
        ...config,
        autoConnect: true,
    }), [config.url, config.network]);
    return (_jsx(EstreamContext.Provider, { value: client, children: children }));
}
/**
 * Get the eStream client from context
 */
export function useEstreamClient() {
    const client = useContext(EstreamContext);
    if (!client) {
        throw new Error('useEstreamClient must be used within an EstreamProvider');
    }
    return client;
}
/**
 * Subscribe to a lattice topic
 *
 * @param topic - Lattice path to subscribe to
 * @param options - Optional transform function
 *
 * @example
 * ```tsx
 * function NetworkTopology() {
 *   const { data: nodes, loading, error } = useSubscription<Node[]>('/console/topology');
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return <TopologyGraph nodes={nodes} />;
 * }
 * ```
 */
export function useSubscription(topic, options) {
    const client = useEstreamClient();
    const [data, setData] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);
    const [refreshKey, setRefreshKey] = useState(0);
    useEffect(() => {
        setLoading(true);
        setError(undefined);
        const unsub = client.subscribe(topic, {
            ...options,
            onData: (newData) => {
                setData(newData);
                setLoading(false);
            },
            onError: (err) => {
                setError(err);
                setLoading(false);
            },
        });
        return unsub;
    }, [topic, refreshKey]);
    const refresh = useCallback(() => {
        setRefreshKey((k) => k + 1);
    }, []);
    return { data, loading, error, refresh };
}
/**
 * Emit data to a lattice topic
 *
 * @param topic - Lattice path to emit to
 *
 * @example
 * ```tsx
 * function SubmitButton() {
 *   const { emit, loading } = useEmit('/my-app/events');
 *
 *   const handleClick = () => {
 *     emit({ type: 'button_click', timestamp: Date.now() });
 *   };
 *
 *   return <button onClick={handleClick} disabled={loading}>Submit</button>;
 * }
 * ```
 */
export function useEmit(topic) {
    const client = useEstreamClient();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const emit = useCallback(async (data) => {
        setLoading(true);
        setError(undefined);
        try {
            await client.emit(topic, data);
        }
        catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [client, topic]);
    return { emit, loading, error };
}
/**
 * Connection state hook
 *
 * @example
 * ```tsx
 * function ConnectionIndicator() {
 *   const { state, isConnected } = useConnection();
 *
 *   return (
 *     <div className={isConnected ? 'connected' : 'disconnected'}>
 *       {state}
 *     </div>
 *   );
 * }
 * ```
 */
export function useConnection() {
    const client = useEstreamClient();
    const [state, setState] = useState(client.state);
    useEffect(() => {
        const handler = () => setState(client.state);
        // Poll for state changes (transport uses EventTarget)
        const interval = setInterval(handler, 500);
        return () => clearInterval(interval);
    }, [client]);
    return {
        state,
        isConnected: state === 'connected',
        network: client.network,
    };
}
/**
 * Submit and track a governance circuit
 *
 * @param circuitId - Circuit identifier
 *
 * @example
 * ```tsx
 * function DeployButton() {
 *   const { submit, status, result } = useCircuit<DeployInput, DeployOutput>('estream.ops.deploy.v1');
 *
 *   const handleDeploy = async () => {
 *     await submit({ version: '1.0.0', environment: 'devnet' });
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleDeploy} disabled={status === 'pending'}>
 *         Deploy
 *       </button>
 *       <span>Status: {status}</span>
 *     </div>
 *   );
 * }
 * ```
 */
export function useCircuit(circuitId) {
    const client = useEstreamClient();
    const [status, setStatus] = useState('idle');
    const [result, setResult] = useState(undefined);
    const [error, setError] = useState(undefined);
    const submissionIdRef = useRef(null);
    // Subscribe to circuit status updates
    useEffect(() => {
        if (!submissionIdRef.current)
            return;
        const unsub = client.subscribe(`/circuits/${submissionIdRef.current}/status`, {
            onData: (data) => {
                const d = data;
                if (d.status === 'approved')
                    setStatus('approved');
                else if (d.status === 'executing')
                    setStatus('executing');
                else if (d.status === 'completed') {
                    setStatus('completed');
                    setResult(d.result);
                }
                else if (d.status === 'failed') {
                    setStatus('failed');
                    setError(new Error(d.error || 'Circuit failed'));
                }
            },
        });
        return unsub;
    }, [submissionIdRef.current]);
    const submit = useCallback(async (input) => {
        setStatus('pending');
        setError(undefined);
        setResult(undefined);
        // Generate submission ID
        const submissionId = crypto.randomUUID();
        submissionIdRef.current = submissionId;
        // Submit circuit
        await client.emit('/circuits/submit', {
            circuitId,
            submissionId,
            input,
        });
        return submissionId;
    }, [client, circuitId]);
    return { submit, status, result, error };
}
// ============================================================================
// Re-exports from other modules
// ============================================================================
// Typed hooks for common data
export * from './hooks.js';
// Spark authentication
export * from './spark.js';
// Spark visual renderer
export * from './spark-renderer.js';
// UI Components
export * from './components.js';
// ESLite WASM hooks (local-first storage)
export * from './eslite-hooks.js';
//# sourceMappingURL=react.js.map